import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Globe, Loader2, PenLine, RefreshCw, Search } from 'lucide-react';
import { listPostmarkIdentities } from './api';
import {
  NEWSLETTER_SENDER_FROM,
  NEWSLETTER_SENDER_REPLY_TO,
} from '../../lib/newsletterSender';
import type { PostmarkIdentity, PostmarkIdentityKind } from './types';

const POSTMARK_SENDERS_URL = 'https://account.postmarkapp.com/signature_domains';

type KindFilter = 'all' | PostmarkIdentityKind;

export default function PostmarkSignaturesView() {
  const [rows, setRows] = useState<PostmarkIdentity[]>([]);
  const [signatureCount, setSignatureCount] = useState(0);
  const [domainCount, setDomainCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [confirmedOnly, setConfirmedOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listPostmarkIdentities();
      setRows(result.identities);
      setSignatureCount(result.signatureCount);
      setDomainCount(result.domainCount);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load identities';
      setError(msg);
      setRows([]);
      setSignatureCount(0);
      setDomainCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((row) => {
      if (kindFilter !== 'all' && row.kind !== kindFilter) return false;
      if (confirmedOnly && !row.confirmed) return false;
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q) ||
        (row.emailAddress?.toLowerCase().includes(q) ?? false) ||
        (row.domain?.toLowerCase().includes(q) ?? false) ||
        (row.replyToEmailAddress?.toLowerCase().includes(q) ?? false) ||
        row.fromLine.toLowerCase().includes(q) ||
        row.kind.includes(q)
      );
    });
  }, [rows, searchQuery, kindFilter, confirmedOnly]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <PenLine className="w-8 h-8 mr-3 text-emerald-500 shrink-0" />
            Postmark Signatures
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Sender signatures and verified domains from your Postmark account. Domains let you
            send from any <span className="font-mono text-xs">*@domain</span> address once DKIM
            and SPF are verified.
          </p>
          {!loading && !error ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {domainCount} domain{domainCount === 1 ? '' : 's'} · {signatureCount} sender
              signature{signatureCount === 1 ? '' : 's'}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search…"
              className="pl-9 pr-3 py-2 w-48 sm:w-56 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center justify-center p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <a
            href={POSTMARK_SENDERS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Postmark
          </a>
        </div>
      </div>

      <div className="mb-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 text-sm text-gray-700 dark:text-gray-300">
        <p className="font-medium text-gray-900 dark:text-white mb-1">Engine default sender</p>
        <p>
          <span className="text-gray-500 dark:text-gray-400">From:</span>{' '}
          <span className="font-mono text-xs">{NEWSLETTER_SENDER_FROM}</span>
        </p>
        <p className="mt-1">
          <span className="text-gray-500 dark:text-gray-400">Reply-To:</span>{' '}
          <span className="font-mono text-xs">{NEWSLETTER_SENDER_REPLY_TO}</span>
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1.5 flex-wrap">
          <KindChip label="All" active={kindFilter === 'all'} onClick={() => setKindFilter('all')} />
          <KindChip
            label={`Domains (${domainCount})`}
            active={kindFilter === 'domain'}
            onClick={() => setKindFilter('domain')}
          />
          <KindChip
            label={`Signatures (${signatureCount})`}
            active={kindFilter === 'signature'}
            onClick={() => setKindFilter('signature')}
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmedOnly}
            onChange={(e) => setConfirmedOnly(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-emerald-500 focus:ring-emerald-500"
          />
          Verified only
        </label>
      </div>

      {error ? (
        <div className="mb-4 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
          <p className="break-words">Could not load identities: {error}</p>
          {error.includes('POSTMARK_ACCOUNT_API_KEY') ? (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400">
              Set <span className="font-mono">POSTMARK_ACCOUNT_API_KEY</span> in Supabase edge
              secrets (Postmark → API Tokens → Account API token).
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 px-2 py-1 text-xs font-medium rounded border border-red-300 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/40"
          >
            Retry
          </button>
        </div>
      ) : null}

      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <PenLine className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {searchQuery || confirmedOnly || kindFilter !== 'all'
              ? 'No matching identities'
              : rows.length === 0
                ? 'No sender identities on this Postmark account'
                : 'Nothing matches your filters'}
          </h3>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[16rem]">
                    Envelope
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map((row) => (
                  <tr key={`${row.kind}-${row.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${
                          row.kind === 'domain'
                            ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200'
                            : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200'
                        }`}
                      >
                        {row.kind === 'domain' ? (
                          <Globe className="w-3 h-3" />
                        ) : (
                          <PenLine className="w-3 h-3" />
                        )}
                        {row.kind === 'domain' ? 'domain' : 'signature'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                      {row.name || <span className="text-gray-400 italic">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      <IdentityEnvelope row={row} />
                    </td>
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {row.kind === 'domain' ? (
                        <DomainStatus row={row} />
                      ) : row.confirmed ? (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                          confirmed
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200 font-mono">
                      {row.id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function IdentityEnvelope({ row }: { row: PostmarkIdentity }) {
  const email =
    row.kind === 'domain'
      ? `any@${row.domain ?? row.name}`
      : row.emailAddress;

  return (
    <div className="space-y-1 text-xs leading-relaxed">
      <EnvelopeLine label="From" value={row.fromLine} />
      <EnvelopeLine label="Email" value={email} />
      <EnvelopeLine
        label="Reply-To"
        value={row.replyToEmailAddress}
        emptyLabel={row.kind === 'domain' ? 'not set on domain' : 'not set'}
      />
    </div>
  );
}

function EnvelopeLine({
  label,
  value,
  emptyLabel = '—',
}: {
  label: string;
  value: string | null | undefined;
  emptyLabel?: string;
}) {
  const trimmed = value?.trim();
  return (
    <p className="flex flex-wrap gap-x-1.5 gap-y-0.5">
      <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}:</span>
      {trimmed ? (
        <span className="font-mono text-gray-800 dark:text-gray-100 break-all">{trimmed}</span>
      ) : (
        <span className="text-gray-400 italic">{emptyLabel}</span>
      )}
    </p>
  );
}

function DomainStatus({ row }: { row: PostmarkIdentity }) {
  const bits = [
    row.dkimVerified ? 'DKIM' : null,
    row.spfVerified ? 'SPF' : null,
    row.returnPathVerified ? 'Return-Path' : null,
  ].filter(Boolean);
  if (row.confirmed) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
        verified
      </span>
    );
  }
  return (
    <span
      className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
      title={bits.length > 0 ? `Has: ${bits.join(', ')}` : 'DKIM + SPF required'}
    >
      partial
    </span>
  );
}

interface KindChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function KindChip({ label, active, onClick }: KindChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      {label}
    </button>
  );
}
